<template>
  <v-card no-body class="mb-3">
    <b-modal ref="infoModal" ok-only :size="infoModalConfig.size">
      <template v-slot:modal-title>
        <h5>{{ infoModalConfig.name }}</h5>
      </template>
      <template v-slot:default>
        <div v-html="infoModalConfig.infoText"></div>
      </template>
    </b-modal>
    <v-card-title class="customer-card-header" :header-bg-variant="headerBgVariant" :header-text-variant="headerTextVariant">
      {{ customerReport.name }} -
      <span v-if="reportPeriod === '1 month'" class="font-weight-normal">
        {{ formatHeaderDate(startDate, 'MMMM YYYY') }} {{ $t('to') }} {{ formatHeaderDate(endDate, 'MMMM YYYY') }} ({{ $t('monthly') }})
      </span>
      <span v-else class="font-weight-normal">
        {{ formatHeaderDate(startDate) }} {{ $t('to') }} {{ formatHeaderDate(endDate) }}
        <span v-if="reportPeriod === '1 week'">({{ $t('weekly') }})</span>
      </span>
    </v-card-title>
    <v-card-text>
      <v-row class="mt-2">
        <v-col class="mb-3" xl="6">
          <v-card no-body :border-variant="cardBorderVariant">
            <v-card-title class="section-card-header" :header-bg-variant="cardHeaderBgVariant" header-text-variant="white">
              <span class="info-text">{{ $t('reports_ui.route_assessment_metrics_report.daily_opened_and_closed_violations') }}</span>
              <v-icon @click="showInfoModal(modalConfigs.dailyOpenedAndClosedViolations)">mdi-information-variant-circle</v-icon>
            </v-card-title>
            <v-card-text>
              <daily-opened-and-closed-violations :customer-report="customerReport"/>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col class="mb-3" xl="6">
          <v-card no-body :border-variant="cardBorderVariant">
            <v-card-title class="section-card-header" :header-bg-variant="cardHeaderBgVariant" header-text-variant="white">
              <span class="info-text">{{ $t('reports_ui.route_assessment_metrics_report.violation_statistic_trends') }}</span>
              <v-icon @click="showInfoModal(modalConfigs.violationStatisticTrends)">mdi-information-variant-circle</v-icon>
            </v-card-title>
            <v-card-text>
              <violation-statistic-trends :customer-report="customerReport"/>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-row class="mt-2">
        <v-col class="mb-3">
          <v-card no-body :border-variant="cardBorderVariant">
            <v-card-title class="section-card-header" :header-bg-variant="cardHeaderBgVariant" header-text-variant="white">
              <span class="info-text">{{ $t('reports_ui.route_assessment_metrics_report.daily_stats') }}</span>
              <v-icon @click="showInfoModal(modalConfigs.dailyStats)">mdi-information-variant-circle</v-icon>
            </v-card-title>
            <v-card-text>
              <daily-stats :customer-report="customerReport"/>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import DailyOpenedAndClosedViolations from '@/components/report/route-assessment-metrics/DailyOpenedAndClosedViolations'
import ViolationStatisticTrends from '@/components/report/route-assessment-metrics/ViolationStatisticTrends'
import DailyStats from '@/components/report/route-assessment-metrics/DailyStats'
import Moment from 'moment'

export default {
  name: 'RouteAssessmentMetricsReportCard',
  components: {
    DailyStats,
    DailyOpenedAndClosedViolations,
    ViolationStatisticTrends
  },
  props: {
    customerReport: {
      type: Object
    },
    startDate: {
      type: String
    },
    endDate: {
      type: String
    },
    timezone: {
      type: String
    },
    reportPeriod: {
      type: String
    },
    comparison: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      infoModalConfig: {}
    }
  },
  computed: {
    headerBgVariant () {
      return this.comparison ? 'secondary' : 'light'
    },
    headerTextVariant () {
      return this.comparison ? 'white' : 'normal'
    },
    cardHeaderBgVariant () {
      return this.comparison ? 'dark' : 'primary'
    },
    cardBorderVariant () {
      return this.comparison ? 'dark' : 'primary'
    },
    /**
     * The text properties in used in the various modal info popups
     */
    modalConfigs () {
      return {
        dailyOpenedAndClosedViolations: {
          size: 'lg',
          name: this.$t('reports_ui.route_assessment_metrics_report.daily_opened_and_closed_violations'),
          infoText: this.$t('reports_ui.route_assessment_metrics_report.modal_info.daily_opened_and_closed_violations')
        },
        violationStatisticTrends: {
          size: 'lg',
          name: this.$t('reports_ui.route_assessment_metrics_report.violation_statistic_trends'),
          infoText: this.$t('reports_ui.route_assessment_metrics_report.modal_info.violation_statistic_trends')
        },
        dailyStats: {
          size: 'lg',
          name: this.$t('reports_ui.route_assessment_metrics_report.daily_stats'),
          infoText: this.$t('reports_ui.route_assessment_metrics_report.modal_info.daily_stats')
        }
      }
    }
  },
  methods: {
    /**
     * Load the selected modal config and show the info modal.
     */
    showInfoModal (config) {
      this.infoModalConfig = config
      this.$refs.infoModal.show()
    },
    formatHeaderDate (date, format = 'll') {
      // parseZone keeps the offset the report was generated with, so the header always matches the range that was
      // selected in the form (and queried on the back end) rather than shifting with the viewer's browser timezone.
      return Moment.parseZone(date).format(format)
    }
  }
}
</script>

<style scoped>
.customer-card-header {
  font-weight: bold;
  font-size: 1.1rem;
}
.section-card-header {
  font-weight: bold;
  font-size: 1rem;
  padding: 0.6rem;
}
.info-icon {
  cursor: pointer;
  justify-content: flex-end;
  align-self: center;
  margin-left: .5rem;
}
.card-table th, .card-table td {
  padding: 0.3rem 1rem;
}
</style>
